import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useCRM } from '../context/CRMContext';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import Card from '../components/UI/Card';
import Button from '../components/UI/Button';
import Modal from '../components/UI/Modal';
import UserForm from '../components/Forms/UserForm';
import PasswordChangeForm from '../components/Forms/PasswordChangeForm';
import SafeIcon from '../common/SafeIcon';
import supabase from '../lib/supabase';
import toast from 'react-hot-toast';
import * as FiIcons from 'react-icons/fi';
import { NotificationService } from '../services/NotificationService';

const { FiPlus, FiEdit, FiTrash2, FiUsers, FiMail, FiPhone, FiShield, FiClock, FiLock, FiCalendar, FiGift, FiDollarSign } = FiIcons;

const Users = () => {
  const { user: currentUser, isAdmin } = useAuth();
  const { departments, settings } = useCRM();
  const navigate = useNavigate();
  
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  
  const [editingUser, setEditingUser] = useState(null);
  const [changingPasswordUserId, setChangingPasswordUserId] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Permission checks
  const canView = isAdmin || currentUser?.permissions?.view_user_management;
  const canCreate = isAdmin || currentUser?.permissions?.create_users;
  const canEdit = isAdmin || currentUser?.permissions?.edit_users;
  const canDelete = isAdmin || currentUser?.permissions?.delete_users;
  const canChangeOtherPassword = isAdmin;

  useEffect(() => {
    if (canView) {
      loadUsers();
    } else {
      setLoading(false);
    }
  }, [canView]);

  const loadUsers = async () => {
    try {
      const { data, error } = await supabase
        .from('users_crm_2025')
        .select(`
          *,
          departments_crm_2025 ( id, name )
        `)
        .order('created_at', { ascending: false });
      
      if (error) {
        throw error;
      }
      
      setUsers(data || []);
    } catch (error) {
      console.error('Error loading users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const getUserName = (u) => u.fullName || u.full_name || '';
  const getInitials = (name) => name ? name.split(' ').map(n => n[0]).join('').toUpperCase() : '?';

  const handleAddUser = () => {
    setEditingUser(null);
    setIsModalOpen(true);
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setIsModalOpen(true);
  };

  const handleChangePassword = (userId) => {
    setChangingPasswordUserId(userId);
    setIsPasswordModalOpen(true);
  };

  const handleManageSalary = (user) => {
    navigate('/payroll', { state: { openSalaryFor: user } });
  };

  const handleSubmit = async (data) => {
    if (submitting) return;
    setSubmitting(true);
    
    try {
      if (!data.full_name || !data.email || !data.role) {
        throw new Error('Please fill in all required fields');
      }

      if (editingUser) {
        const updateData = {
          full_name: data.full_name.trim(),
          email: data.email.toLowerCase().trim(),
          role: data.role,
          department_id: data.department_id || null,
          phone: data.phone || null,
          date_of_joining: data.date_of_joining || null,
          date_of_birth: data.date_of_birth || null,
          is_active: data.is_active !== false,
        };

        if (data.newPassword) {
          updateData.password_hash = data.newPassword;
          
          const { error: logError } = await supabase
            .from('password_changes_crm_2025')
            .insert([{
              user_id: editingUser.id,
              old_password_hash: null,
              new_password_hash: data.newPassword,
              changed_by: currentUser.id,
            }]);
            
          if (logError) console.warn('Failed to log password change:', logError);
        }

        const { error } = await supabase
          .from('users_crm_2025')
          .update(updateData)
          .eq('id', editingUser.id);

        if (error) throw error;
        
        let successMsg = 'User updated successfully';
        if (data.newPassword) successMsg += ' and password reset';
        toast.success(successMsg);
        
        if (data.newPassword) {
          NotificationService.sendPasswordChangedEmail(editingUser, settings);
        }
      } else {
        // Correctly generate lowercase default password (e.g., 'admin123', not 'Admin123')
        const defaultPassword = `${data.role.toLowerCase()}123`;
        
        const userData = {
          full_name: data.full_name.trim(),
          email: data.email.toLowerCase().trim(),
          password_hash: defaultPassword,
          role: data.role,
          department_id: data.department_id || null,
          phone: data.phone || null,
          date_of_joining: data.date_of_joining || null,
          date_of_birth: data.date_of_birth || null,
          is_active: data.is_active !== false,
        };

        const { data: newUser, error } = await supabase
          .from('users_crm_2025')
          .insert([userData])
          .select()
          .single();

        if (error) throw error;
        
        toast.success(`User created successfully. Default password: ${defaultPassword}`);
        await NotificationService.sendNewUserWelcomeEmail(newUser, defaultPassword, settings);
      }

      await loadUsers();
      setIsModalOpen(false);
      setEditingUser(null);
    } catch (error) {
      console.error('Error saving user:', error);
      if (error.code === '23505') {
        toast.error('Email already exists. Please use a different email.');
      } else if (error.message) {
        toast.error(error.message);
      } else {
        toast.error('Failed to save user. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (userId === currentUser.id) {
      toast.error('You cannot delete your own account');
      return;
    }
    
    if (window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      try {
        const { error } = await supabase
          .from('users_crm_2025')
          .delete()
          .eq('id', userId);
          
        if (error) throw error;
        
        setUsers(prev => prev.filter(user => user.id !== userId));
        toast.success('User deleted successfully');
      } catch (error) {
        console.error('Error deleting user:', error);
        toast.error('Failed to delete user');
      }
    }
  };

  const toggleUserStatus = async (userId, currentStatus) => {
    if (!canEdit) return;
    
    try {
      const { error } = await supabase
        .from('users_crm_2025')
        .update({ is_active: !currentStatus })
        .eq('id', userId);
        
      if (error) throw error;
      
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, is_active: !currentStatus } : u));
      toast.success(`User ${!currentStatus ? 'activated' : 'deactivated'} successfully`);
    } catch (error) {
      console.error('Error updating user status:', error);
      toast.error('Failed to update user status');
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'Admin': return 'bg-red-100 text-red-800';
      case 'Manager': return 'bg-blue-100 text-blue-800';
      case 'Employee': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'Admin': return FiShield;
      case 'Manager': return FiUsers;
      case 'Employee': return FiUsers;
      default: return FiUsers;
    }
  };

  const getDepartmentName = (user) => {
    if (user.departments_crm_2025) {
      return user.departments_crm_2025.name;
    }
    return user.department || 'No Department';
  };

  if (!canView) {
    return (
      <div className="space-y-6">
        <Card className="p-12 text-center">
          <SafeIcon icon={FiShield} className="h-12 w-12 text-red-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Access Denied</h3>
          <p className="text-gray-500">You don't have permission to access user management.</p>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading users...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900">User Management</h1>
        {canCreate && (
          <Button onClick={handleAddUser} disabled={submitting}>
            <SafeIcon icon={FiPlus} className="h-4 w-4 mr-2" /> Add User
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-lg bg-blue-50"><SafeIcon icon={FiUsers} className="h-6 w-6 text-blue-600" /></div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Users</p>
              <p className="text-2xl font-bold text-gray-900">{users.length}</p>
            </div>
          </div>
        </Card>
        <Card className="p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-lg bg-red-50"><SafeIcon icon={FiShield} className="h-6 w-6 text-red-600" /></div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Admins</p>
              <p className="text-2xl font-bold text-gray-900">
                {users.filter(u => u.role === 'Admin').length}
              </p>
            </div>
          </div>
        </Card>
        <Card className="p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-lg bg-blue-50"><SafeIcon icon={FiUsers} className="h-6 w-6 text-blue-600" /></div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Managers</p>
              <p className="text-2xl font-bold text-gray-900">
                {users.filter(u => u.role === 'Manager').length}
              </p>
            </div>
          </div>
        </Card>
        <Card className="p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-lg bg-green-50"><SafeIcon icon={FiUsers} className="h-6 w-6 text-green-600" /></div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Employees</p>
              <p className="text-2xl font-bold text-gray-900">
                {users.filter(u => u.role === 'Employee').length}
              </p>
            </div>
          </div>
        </Card>
      </div>

      {users.length === 0 ? (
        <Card className="p-12 text-center">
          <SafeIcon icon={FiUsers} className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No users found</h3>
          {canCreate ? (
            <>
              <p className="text-gray-500 mb-4">Get started by adding your first user.</p>
              <Button onClick={handleAddUser}><SafeIcon icon={FiPlus} className="h-4 w-4 mr-2" /> Add User</Button>
            </>
          ) : (
            <p className="text-gray-500">Contact an administrator to add users.</p>
          )}
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {users.map((user, index) => (
            <motion.div 
              key={user.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <Card className={`p-6 hover:shadow-lg transition-shadow ${!user.is_active ? 'opacity-60' : ''}`}>
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="h-10 w-10 bg-primary-600 rounded-full flex items-center justify-center">
                      <span className="text-white font-medium">{getInitials(getUserName(user))}</span>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        {getUserName(user)} {user.id === currentUser.id && (<span className="ml-2 text-sm text-blue-600">(You)</span>)}
                      </h3>
                      <div className="flex items-center space-x-2">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getRoleColor(user.role)}`}>
                          <SafeIcon icon={getRoleIcon(user.role)} className="h-3 w-3 mr-1 inline" />{user.role}
                        </span>
                        {!user.is_active && (<span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">Inactive</span>)}
                      </div>
                    </div>
                  </div>
                  <div className="flex space-x-1">
                    {/* Admin Salary Action */}
                    {isAdmin && (
                      <button onClick={() => handleManageSalary(user)} className="text-gray-400 hover:text-green-600 transition-colors p-1" title="Manage Salary">
                        <SafeIcon icon={FiDollarSign} className="h-4 w-4" />
                      </button>
                    )}
                    {canEdit && (
                      <button onClick={() => handleEditUser(user)} className="text-gray-400 hover:text-primary-600 transition-colors p-1" title="Edit User">
                        <SafeIcon icon={FiEdit} className="h-4 w-4" />
                      </button>
                    )}
                    {canChangeOtherPassword && (
                      <button onClick={() => handleChangePassword(user.id)} className="text-gray-400 hover:text-blue-600 transition-colors p-1" title="Change Password">
                        <SafeIcon icon={FiLock} className="h-4 w-4" />
                      </button>
                    )}
                    {canDelete && user.id !== currentUser.id && (
                      <button onClick={() => handleDeleteUser(user.id)} className="text-gray-400 hover:text-red-600 transition-colors p-1" title="Delete User">
                        <SafeIcon icon={FiTrash2} className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
                
                <div className="space-y-2">
                  <div className="flex items-center text-sm text-gray-600"><SafeIcon icon={FiMail} className="h-4 w-4 mr-2" /><span>{user.email}</span></div>
                  {user.phone && (<div className="flex items-center text-sm text-gray-600"><SafeIcon icon={FiPhone} className="h-4 w-4 mr-2" /><span>{user.phone}</span></div>)}
                  <div className="flex items-center text-sm text-gray-600"><SafeIcon icon={FiUsers} className="h-4 w-4 mr-2" /><span>{getDepartmentName(user)}</span></div>
                  {user.date_of_joining && (<div className="flex items-center text-sm text-gray-600"><SafeIcon icon={FiCalendar} className="h-4 w-4 mr-2" /><span>Joined: {format(new Date(user.date_of_joining), 'MMM dd, yyyy')}</span></div>)}
                  {user.date_of_birth && (<div className="flex items-center text-sm text-gray-600"><SafeIcon icon={FiGift} className="h-4 w-4 mr-2" /><span>DOB: {format(new Date(user.date_of_birth), 'MMM dd, yyyy')}</span></div>)}
                  {user.last_login && (<div className="flex items-center text-sm text-gray-600"><SafeIcon icon={FiClock} className="h-4 w-4 mr-2" /><span>Last login: {format(new Date(user.last_login), 'MMM dd, yyyy')}</span></div>)}
                </div>

                <div className="mt-4 pt-4 border-t border-gray-100 flex justify-between items-center">
                  <p className="text-xs text-gray-500">Created {format(new Date(user.created_at), 'MMM dd, yyyy')}</p>
                  {canEdit && user.id !== currentUser.id && (
                    <Button 
                      size="sm" 
                      variant={user.is_active ? "outline" : "primary"}
                      onClick={() => toggleUserStatus(user.id, user.is_active)}
                    >
                      {user.is_active ? 'Deactivate' : 'Activate'}
                    </Button>
                  )}
                </div>
              </Card>
            </motion.div>
          ))}
        </div>
      )}

      <Modal 
        isOpen={isModalOpen} 
        onClose={() => {
          if (!submitting) {
            setIsModalOpen(false);
            setEditingUser(null);
          }
        }} 
        title={editingUser ? 'Edit User' : 'Add New User'} 
        size="lg"
      >
        <UserForm 
          user={editingUser} 
          onSubmit={handleSubmit} 
          onCancel={() => {
            if (!submitting) {
              setIsModalOpen(false);
              setEditingUser(null);
            }
          }}
          submitting={submitting}
        />
      </Modal>

      <Modal
        isOpen={isPasswordModalOpen}
        onClose={() => setIsPasswordModalOpen(false)}
        title="Change Password"
        size="md"
      >
        <PasswordChangeForm 
          userId={changingPasswordUserId} 
          onCancel={() => setIsPasswordModalOpen(false)}
          onSuccess={() => {
            setIsPasswordModalOpen(false);
            setChangingPasswordUserId(null);
          }}
        />
      </Modal>
    </div>
  );
};

export default Users;